import { loadEnvConfig } from "@next/env";
import Head from "next/head";
import Script from "next/script";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import requestIP from "request-ip";
import NextNProgress from "nextjs-progressbar";

import "../styles/global.css";
import wrapper from "../redux/store";
import {
  getBrainTreeCoupons,
  getIsMembership,
  getSubscriptionDetails,
  getUserProfile,
  getBrainTreePlan,
  getRevenueCatPlans,
} from "apis/apiGetMe";
import {
  checkCouponDetails,
  setCoupon,
  setUserProfile,
} from "redux/auth/auth.actions";
import axiosInstance from "utils/axios";
import { getTokenFromCookies } from "utils/cookiesHelper";
import { setYearlyPlan } from "redux/globalUI/globalUI.actions";
import allPagesContent from "contents.json";
import i18n from "i18n";
import {
  getClientCountryCode,
  handleSetLocalLanguage,
} from "utils/locationHelpers";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
} from "constants/languages.constant";

//* Analytics

import { getAnalytics, setCurrentScreen } from "utils/segmentHelpers";
import {
  BRAINTREE,
  COUNTRIES_IN_REGION,
  COUNTRIES_SUPPORT_PAYPAL,
  REVENUE_CAT,
} from "constants/plans.constants";
import { getCloudFrontKeyPairId, setToken } from "utils/cookieHelpers";

const origin = process.env.NEXT_PUBLIC_SITE_URL;
const ogDescription =
  "Mindset is a safe space where artists share stories of their journeys with wellness and mental health, and how they have overcome many of life’s challenges. Remember, sometimes we don’t need advice, we just need to hear that we are not alone.";

const AppRenderer = ({
  Component,
  pageProps,
  userProfile,
  isMembership,
  subscriptionDetails,
  coupons,
  token,
  plan,
}) => {
  const router = useRouter();
  const [language, setLanguage] = useState(router.locale);
  const dispatch = useDispatch();

  const couponCode = useMemo(() => {
    return router.query.coupon;
  }, [router.query]);

  const userPreferLanguage = userProfile?.setting?.languageCode;

  useEffect(() => {
    //* Check if user has a preferred language
    //* If not use default locale language, else use preferred language
    i18n.changeLanguage(userPreferLanguage || router.locale);
    // setLanguage(userPreferLanguage || router.locale);
    handleSetLocalLanguage(userPreferLanguage || language);
  }, [router.locale]);

  useEffect(() => {
    setCurrentScreen(router.asPath);
    router.events.on("routeChangeStart", (nextRoute) => {
      setCurrentScreen(nextRoute);
    });
  }, []);

  const { title, description, pageName, url } = useMemo(() => {
    if (!allPagesContent) return {};
    const { default: defaultContent } = allPagesContent;
    const currentPageContent = allPagesContent[router.asPath];
    return {
      title: currentPageContent?.title || defaultContent.title,
      description:
        currentPageContent?.description || defaultContent.description,
      pageName: currentPageContent?.pageName || defaultContent.pageName,

      url: process.env.NEXT_PUBLIC_SITE_URL + router.asPath,
    };
  }, [allPagesContent, router.asPath]);

  useEffect(() => {
    const analytics = getAnalytics();
    analytics.page(pageName, {
      title,
      url: window.location.href,
      path: router.asPath,
    });
  }, [title, router.asPath]);

  useEffect(() => {
    dispatch(
      setUserProfile({
        userProfile,
        isMembership,
        subscriptionDetails,
        coupon: null,
      })
    );
  }, []);

  useEffect(() => {
    const handleRouteChange = (url) => {};

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    if (!plan) {
      return;
    }
    dispatch(setYearlyPlan(plan));
    if (plan.type === BRAINTREE) {
      dispatch(setCoupon(coupons.find((coupon) => coupon.id === couponCode)));
    } else {
      couponCode && dispatch(checkCouponDetails(couponCode));
    }
  }, [couponCode, plan, coupons]);

  return (
    <>
      <Head>
        <link rel="shortcut icon" type="image/x-icon" href="/favicon.png" />
        <link href="/webclip.png" rel="apple-touch-icon" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        {/* Open Graph tags */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={ogDescription} />
        <meta
          property="og:image"
          content={`${origin}/images/meta_facebook.png`}
        />

        {/* Twitter tags */}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={ogDescription} />
        <meta
          name="twitter:image"
          content={`${origin}/images/meta_twitter.png`}
        />
        <meta name="twitter:card" content="summary" />
      </Head>
      <Script
        id="segmentScript"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${process.env.NEXT_PUBLIC_SEGMENT_APP_ID}";;analytics.SNIPPET_VERSION="4.15.3";
          analytics.load("${process.env.NEXT_PUBLIC_SEGMENT_APP_ID}");
          }}();
        `,
        }}
      />

      <NextNProgress
        color="#00bb56"
        height={5}
        options={{ showSpinner: false }}
      />
      <Component
        {...pageProps}
        userProfile={userProfile}
        isMembership={isMembership}
        allPagesContent={allPagesContent}
        subscriptionDetails={subscriptionDetails}
      />
    </>
  );
};

const App = (props) => {
  return <AppRenderer {...props} />;
};

App.getInitialProps = async ({
  ctx: { req, res, locale = "", ...rest },
  router,
}) => {
  /**
   * Check if the user is authenticated
   *
   * 1. check for the cookier
   * 2. check if the user cookie is valid.
   *    - if it is not, remove it from the axios headers.
   */
  const cookie = req ? req.headers.cookie : null;
  let token;
  if (!!cookie) {
    token = getTokenFromCookies(cookie);
    if (token) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common.Authorization;
    }
  } else {
    delete axiosInstance.defaults.headers.common.Authorization;
  }

  let countryCode;

  if (req && res) {
    const { asPath = "/", pathname } = router;
    if (pathname !== "/_error") {
      // set default countryCode to US, to return highest price if there is any error when searching location
      if (req.cookies.COUNTRY_CODE) {
        countryCode = req.cookies.COUNTRY_CODE;
      } else {
        countryCode = "US";
        try {
          const clientIP = requestIP.getClientIp(req);
          countryCode = await getClientCountryCode(clientIP);
        } catch (err) {
          console.error("err", err);
        }

        // Only redirect if there is no countryCode in cookies
        if (!locale.toLowerCase().includes(countryCode.toLowerCase())) {
          const correctLocale = SUPPORTED_LOCALES.find((locale) =>
            locale.toLowerCase().includes(countryCode.toLowerCase())
          );

          if (correctLocale) {
            // It will take more time at first load
            if (correctLocale === DEFAULT_LOCALE) {
              // default locale
              res
                .writeHead(302, { Location: `/${correctLocale}${asPath}` })
                .end();
            } else {
              res
                .writeHead(302, { Location: `/${correctLocale}${asPath}` })
                .end();
            }
            return {};
          }
        }
      }
    }
  }

  let plan = null;
  let coupons = [];
  if (COUNTRIES_SUPPORT_PAYPAL.includes(countryCode)) {
    console.log(`Fetching Paypal/Braintree Plans: countryCode=${countryCode}`);
    const planId = COUNTRIES_IN_REGION.includes(countryCode)
      ? "mindset_membership_regions"
      : "mindset_membership_other";
    plan = await getBrainTreePlan(planId);
    coupons = await getBrainTreeCoupons();
  } else {
    console.log(`Fetching RevenueCat Plans: countryCode=${countryCode}`);
    let plans = await getRevenueCatPlans();
    let rc_products = plans[0].products;
    let rc_product_id_map = {};
    rc_products.forEach((p) => {
      rc_product_id_map[p["id"]] = p;
    });
    let product_id = "base_15_99";
    if (COUNTRIES_IN_REGION.includes(countryCode)) {
      product_id = "regions_5_99";
    }
    console.log(
      `Finding Plan for: ${countryCode} and product_id: ${product_id}`
    );
    plan = rc_product_id_map[product_id];
  }

  if (plan) {
    plan.type = COUNTRIES_SUPPORT_PAYPAL.includes(countryCode)
      ? BRAINTREE
      : REVENUE_CAT;
  }

  const userProfile = token ? await getUserProfile() : null;
  const isMembership = token ? await getIsMembership() : false;
  const subscriptionDetails = token ? await getSubscriptionDetails() : null;

  return {
    userProfile,
    isMembership,
    subscriptionDetails,
    coupons,
    countryCode,
    token,
    plan,
  };
};

export default wrapper.withRedux(App);
